import { ReferralShareLocation } from 'common/events/ClientEvents';
import React from 'react';
import { useCurrentUser } from 'src/shared/hooks/useCurrentUserHook';
import { useReferralShare } from 'src/shared/hooks/useReferralShare';
import Button, { ButtonType } from 'web-common/src/shared/design-system/components/Button';

interface ReferralShareButtonProps {
  analyticsLocation: ReferralShareLocation;
  share?: () => void;
  copyText?: React.ReactNode;
  onButtonClick?: () => void;
}

/**
 * A reusable referral share button component that can be used in two different contexts:
 *
 * 1. **Standalone Usage**: When used without optional props, the button manages its own share state
 *    internally using the `useReferralShare` hook. This is ideal for independent usage where
 *    the button is the only share mechanism on the page.
 *
 * 2. **Shared State Usage**: When used with optional props (share, copyText, currentUser),
 *    the button uses the provided shared state instead of creating its own. This allows the
 *    button to be used within other components, like clickable banners, where we want the button
 *    to react to the same shared state. The button automatically prevents event bubbling to
 *    avoid double execution when clicked inside parent clickable components.
 *
 * @param analyticsLocation - The location context for analytics tracking
 * @param share - Optional share function. If provided, uses this instead of internal hook
 * @param copyText - Optional shared copy text state. If provided, uses this instead of internal hook
 * @param onButtonClick - Optional callback fired when button is clicked. Useful for preventing parent click handlers
 */
export const ReferralShareButton: React.FC<ReferralShareButtonProps> = ({
  analyticsLocation,
  share: externalShare,
  copyText: externalCopyText,
  onButtonClick,
}) => {
  const [currentUser] = useCurrentUser();
  const internalReferralShare = useReferralShare(analyticsLocation);
  const share = externalShare ?? internalReferralShare.share;
  const copyText = externalCopyText ?? internalReferralShare.copyText;

  if (!currentUser) return null;

  const handleClick = () => {
    onButtonClick?.();
    share();
  };

  return (
    <Button
      buttonType={ButtonType.Secondary}
      onClick={handleClick}
      align='center'
      iconName='share'
      iconPosition='right'>
      {copyText}
    </Button>
  );
};
